import { useState } from "react";
import { ShieldCheck, Lock, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordForm = ({ email, emailSent, displayCode, onSuccess, onBack }) => {
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!resetCode.trim()) { setError("Please enter the reset code"); return; }
    if (newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        email, code: resetCode.trim(), new_password: newPassword
      });
      toast.success(data.message);
      onSuccess(email);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={onBack} className="flex items-center gap-1 mb-4 text-sm" style={{ color: "var(--color-text-dim)" }}>
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="w-8 h-8" style={{ color: "var(--color-accent)" }} />
        <h2 className="text-2xl font-black" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Enter Reset Code
        </h2>
      </div>

      {emailSent ? (
        <p className="mb-6" style={{ color: "var(--color-text-muted)" }}>
          A 6-digit code has been sent to <strong style={{ color: "var(--color-text)" }}>{email}</strong>. Check your inbox.
        </p>
      ) : displayCode ? (
        <>
          <p className="mb-3" style={{ color: "var(--color-text-muted)" }}>Email delivery failed. Use the code below:</p>
          <div className="mb-6 p-4 rounded-lg text-center" style={{ backgroundColor: "var(--color-accent-glow)", border: "1px solid var(--color-accent)" }}>
            <p className="text-xs mb-1" style={{ color: "var(--color-text-dim)" }}>Your reset code</p>
            <p className="text-3xl font-black tracking-[0.3em]" style={{ color: "var(--color-accent)" }} data-testid="reset-code-display">
              {displayCode}
            </p>
          </div>
        </>
      ) : (
        <p className="mb-6" style={{ color: "var(--color-text-muted)" }}>
          Check <strong style={{ color: "var(--color-text)" }}>{email}</strong> for your reset code.
        </p>
      )}

      {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="reset-error">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Reset Code</label>
          <Input type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000" className="text-center text-2xl tracking-[0.3em] rounded-lg font-bold"
            style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
            data-testid="reset-code-input" maxLength={6} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>New Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "var(--color-text-dim)" }} />
            <Input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 6 characters" className="pl-10 pr-10 rounded-lg"
              style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              data-testid="new-password-input" required minLength={6} />
            <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
              style={{ color: "var(--color-text-dim)" }} data-testid="toggle-new-password">
              {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading || resetCode.length !== 6}
          className="w-full rounded-lg py-3 font-bold text-white"
          style={{ backgroundColor: "var(--color-accent)" }} data-testid="reset-password-button">
          {loading ? "Resetting..." : "Reset Password"}
        </Button>
      </form>
    </>
  );
};

export default ResetPasswordForm;
